import React from 'react';
import { MuiTelInput } from 'mui-tel-input';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import classes from './_buyChallenge.module.scss';

import { DiscountInfo } from './BuyChallenge';

interface BlackFriday100kInfo {
  quantityInCart: number;
  freeChallenges: number;
  totalReceived: number;
}

interface BuyChallengeFormProps {
  // Translation function (i18n)
  t: any;

  // Logged user data (used to prefill some fields)
  user: any;

  // Wallet tokens or similar feature, used to show virtual wallet checkbox
  tokens: any;

  // List of country names displayed in the select
  countryOptions: string[];

  // Currently selected country (we treat it as ISO2 code, e.g. "MT", "FR")
  selectedOption: string | undefined;

  // Setter to update the selected country (ISO2 code)
  setSelectedOption: (value: string) => void;

  // Phone number and validation
  phone: string;
  phoneError: string;
  handlePhoneChange: (value: string) => void;

  // Payment method select
  selectedPayment: string | undefined;
  setSelectedPayment: (value: string) => void;
  paymentOptions: string[];

  // Promo code handling
  promoCode: string;
  promoError: string;
  handlePromoCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleApplyPromoCode: () => void;

  // Pricing handling
  calculateTotalCost: () => number;
  discountInfo: DiscountInfo | null;

  // Virtual wallet usage
  useVirtualWallet: boolean;
  setUseVirtualWallet: (value: boolean) => void;
  walletDeduction: number;

  // Payment submission handler
  handlePayment: (event: React.FormEvent<HTMLFormElement>) => void;

  // Helper to map a country name to an ISO2 code if needed (e.g. "France" -> "FR")
  getCode: (countryName: string) => string | undefined;
  blackFriday100kInfo: BlackFriday100kInfo | null;

}

// Type used for the Autocomplete country options
interface CountryOption {
  label: string; // Country name, e.g. "France"
  code: string;  // ISO2 code, e.g. "FR"
}

const BuyChallengeForm: React.FC<BuyChallengeFormProps> = ({
  t,
  user,
  tokens,
  countryOptions,
  selectedOption,
  setSelectedOption,
  phone,
  phoneError,
  handlePhoneChange,
  selectedPayment,
  setSelectedPayment,
  paymentOptions,
  promoCode,
  promoError,
  handlePromoCodeChange,
  handleApplyPromoCode,
  calculateTotalCost,
  discountInfo,
  useVirtualWallet,
  setUseVirtualWallet,
  walletDeduction,
  handlePayment,
  getCode,
  blackFriday100kInfo,
}) => {
  // Special cases for country names that don't match the country-list library
  const specialCases: Record<string, string> = {
    Bolivia: 'BO',
    'British Virgin Islands': 'VG',
    Brunei: 'BN',
    'Cape Verde': 'CV',
    'Caribbean Netherlands': 'BQ',
    'DR Congo': 'CD',
    'Falkland Islands': 'FK',
    'French Southern and Antarctic Lands': 'TF',
    'Ivory Coast': 'CI',
    Kosovo: 'XK',
    Macao: 'MO',
    Micronesia: 'FM',
    Moldova: 'MD',
    Palestine: 'PS',
    'Pitcairn Islands': 'PN',
    'Republic of the Congo': 'CG',
    'Sint Maarten': 'SX',
    'Saint Martin': 'MF',
    'South Georgia': 'GS',
    'South Korea': 'KR',
    'São Tomé and Príncipe': 'ST',
    Taiwan: 'TW',
    Turkey: 'TR',
    Tanzania: 'TZ',
    'United Kingdom': 'GB',
    'Vatican City': 'VA',
    Venezuela: 'VE',
  };

  // Check if a string looks like an ISO2 code (e.g. "FR", "MT")
  const isIso2 = (value: string): boolean =>
    /^[A-Z]{2}$/.test(value.trim());

  // Central helper to map a country display name to an ISO2 code
  const getIsoFromName = (name: string): string => {
    const trimmed = name?.trim();
    if (!trimmed) return '';
    if (specialCases[trimmed]) return specialCases[trimmed];
    return getCode(trimmed) || '';
  };

  /**
   * Build the list of country options for the Autocomplete
   * from the provided countryOptions array and mapping to ISO2.
   */
  const countries: CountryOption[] = React.useMemo(
    () =>
      countryOptions.map((name) => ({
        label: name,
        code: getIsoFromName(name),
      })),
    [countryOptions, getCode],
  );

  /**
   * Determine which country should be selected by default in the Autocomplete.
   *
   * We treat selectedOption as an ISO2 code (e.g. "MT", "FR").
   * If selectedOption is empty, we fall back to user.country.
   * user.country can be either an ISO2 code ("FR") or a label ("France").
   */
  const selectedCountryOption = React.useMemo(() => {
    // 1) Try selectedOption as ISO2
    if (selectedOption) {
      const byCode = countries.find((c) => c.code === selectedOption);
      if (byCode) return byCode;
    }

    // 2) Try user.country
    if (user?.country) {
      const raw = String(user.country).trim();

      // If user.country already looks like an ISO2 code, use it directly
      if (isIso2(raw)) {
        const byCode = countries.find((c) => c.code === raw);
        if (byCode) return byCode;
      }

      // Otherwise, treat it as a country name and map it
      const isoFromUser = getIsoFromName(raw);
      if (isoFromUser) {
        const byCode = countries.find((c) => c.code === isoFromUser);
        if (byCode) return byCode;
      }

      // Last fallback: match by label
      const byLabel = countries.find((c) => c.label === raw);
      if (byLabel) return byLabel;
    }

    return null;
  }, [countries, selectedOption, user, getCode]);

  /**
   * Initial country for the phone input.
   *
   * Computed only once:
   *  - try selectedOption (ISO2)
   *  - else use user.country (ISO2 or label) and map to ISO2
   *
   * Changing the country in the autocomplete will NOT update this.
   */
  const [initialPhoneCountry] = React.useState<string | undefined>(() => {
    if (selectedOption) {
      return selectedOption;
    }

    if (user?.country) {
      const raw = String(user.country).trim();

      // If already ISO2, use it directly
      if (isIso2(raw)) {
        return raw;
      }

      // Else map from label to ISO2
      const iso = getIsoFromName(raw);
      if (iso) {
        return iso;
      }
    }

    return undefined;
  });

  return (
    <Card
      elevation={0}
      className={classes.sidebar}
      sx={{ padding: '16px' }}
    >
      <Typography variant="h5" component="h5" mb={4} fontWeight="bold">
        {t('billingInfoCard.billingInfo')}
      </Typography>

      <form onSubmit={handlePayment}>
        {/* First name / Last name */}
        <Box display="flex" justifyContent="space-between" mb={3}>
          <TextField
            id="firstname"
            name="firstname"
            label={t('billingInfoCard.firstname')}
            defaultValue={user?.firstName}
            variant="outlined"
            fullWidth
            required
            sx={{ mr: 1 }}
          />
          <TextField
            id="lastname"
            name="lastname"
            label={t('billingInfoCard.lastname')}
            defaultValue={user?.lastName}
            variant="outlined"
            fullWidth
            required
            sx={{ ml: 1 }}
          />
        </Box>

        {/* Country (with flags) / City */}
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Autocomplete
            id="country"
            // Controlled value: must be one of the options or null
            value={selectedCountryOption}
            // Update selectedOption with the ISO2 code when the user selects a country
            onChange={(_event, newValue) => {
              setSelectedOption(newValue?.code ?? '');
            }}
            options={countries}
            autoHighlight
            getOptionLabel={(option) => option.label}
            sx={{ width: '100%', mr: 1 }}
            renderOption={(props, option) => (
              <Box
                component="li"
                sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                {...props}
              >
                {/* Show flag only if the code looks like a valid ISO2 */}
                {option.code && option.code.length === 2 && (
                  <img
                    loading="lazy"
                    width="20"
                    src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                    alt=""
                  />
                )}
                {option.label}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('billingInfoCard.country')}
                required
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password', // disable browser autocomplete
                }}
              />
            )}
          />

          <TextField
            id="city"
            name="city"
            label={t('billingInfoCard.city')}
            defaultValue={user?.city}
            variant="outlined"
            fullWidth
            required
            sx={{ ml: 1 }}
          />
        </Box>

        {/* Street / Postal code */}
        <Box display="flex" justifyContent="space-between" mb={3}>
          <TextField
            id="street"
            name="street"
            label={t('billingInfoCard.street')}
            defaultValue={user?.street}
            variant="outlined"
            fullWidth
            required
            sx={{ mr: 1 }}
          />
          <TextField
            id="postalCode"
            name="postalCode"
            label={t('billingInfoCard.postalCode')}
            defaultValue={user?.postalCode}
            variant="outlined"
            fullWidth
            required
            sx={{ ml: 1 }}
          />
        </Box>

        {/* Email */}
        <Box mb={3}>
          <TextField
            id="email"
            name="email"
            label={t('billingInfoCard.emailLabel')}
            defaultValue={user?.email}
            variant="outlined"
            fullWidth
            required
          />
        </Box>

        {/* Phone number */}
        <Box mb={2}>
          <MuiTelInput
            id="phone"
            name="phone"
            label={t('billingInfoCard.PHONE_NUMBER_LABEL')}
            defaultCountry={initialPhoneCountry as any}
            value={phone}
            onChange={handlePhoneChange}
            error={!!phoneError}
            helperText={phoneError}
            fullWidth
          />

          <Divider sx={{ mt: 4, mb: 4 }} />
        </Box>

        {/* Payment method */}
        <Box mb={3}>
          <TextField
            id="paymentMethod"
            name="paymentMethod"
            select
            label={t('billingInfoCard.paymentMethod')}
            value={selectedPayment}
            onChange={(e) => setSelectedPayment(e.target.value)}
            variant="outlined"
            fullWidth
          >
            {paymentOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Promo code */}
        <Stack direction="row" spacing={2} mb={3}>
          <TextField
            id="couponCode"
            label={t('billingInfoCard.couponCode')}
            value={promoCode}
            onChange={handlePromoCodeChange}
            variant="outlined"
            fullWidth
            sx={{ flex: 2 }}
            error={!!promoError}
            helperText={promoError || ''}
          />

          <Button
            variant="contained"
            onClick={handleApplyPromoCode}
            sx={{
              backgroundColor: 'white',
              color: 'black',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
          >
            {t('apply', { ns: 'common' })}
          </Button>
        </Stack>

        {/* Platform info */}
        <Stack justifyContent="space-between" direction="row">
          <Typography
            variant="subtitle1"
            component="p"
            fontWeight="bold"
            gutterBottom
          >
            {t('billingInfoCard.platform')}:
          </Typography>
          <Typography
            variant="subtitle1"
            component="p"
            fontWeight="bold"
            gutterBottom
          >
            Metatrader 5
          </Typography>
        </Stack>

        {/* Price info */}
        <Stack justifyContent="space-between" direction="row">
          <Typography
            variant="subtitle1"
            component="p"
            fontWeight="bold"
            gutterBottom
          >
            {t('billingInfoCard.price')}:
          </Typography>
          <Typography
            variant="subtitle1"
            component="p"
            fontWeight="bold"
            gutterBottom
          >
            {calculateTotalCost()} €
          </Typography>
        </Stack>

        {/* Discount summary */}
        {discountInfo && (
          <Typography
            variant="body1"
            component="p"
            color="#1FCC92"
            gutterBottom
          >
            -{discountInfo.discountValue}€ ({discountInfo.discountPercentage}% Off)
          </Typography>
        )}

        {/* Wallet deduction summary */}
        {useVirtualWallet && (
          <Typography
            variant="body1"
            component="p"
            color="#1FCC92"
            gutterBottom
          >
            -{walletDeduction}€ (from virtual wallet)
          </Typography>
        )}

        {/* Use virtual wallet checkbox */}
        {tokens && (
          <FormControlLabel
            control={
              <Checkbox
                checked={useVirtualWallet}
                onChange={(e) => setUseVirtualWallet(e.target.checked)}
              />
            }
            label={t('useWalletCard.useWalletButton')}
          />
        )}

        {blackFriday100kInfo && (
          <Typography
            variant="body2"
            component="p"
            color="#1FCC92"
            mt={1}
          >
            {t('billingInfoCard.BLACK_FRIDAY_100K_INFO', {
              total: blackFriday100kInfo.totalReceived,
              free: blackFriday100kInfo.freeChallenges,
            })}
          </Typography>
        )}

        {/* Submit button */}
        <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>
          {t('billingInfoCard.paymentButton')}
        </Button>
      </form>
    </Card>
  );
};

export default BuyChallengeForm;
